import React, { useMemo, useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { userInformationAtom } from '../Services/_states';
import { authzContextAtom } from './authzState';
import { buildAuthzContext, extractGrantsFromUser } from './buildAuthzContext';
import { canAccess, canAccessAny } from './canAccess';
import { canAccessResource } from './canAccessResource';

const AuthorizationContext = React.createContext(null);

export function AuthorizationProvider({ children }) {
  const userInfo = useRecoilValue(userInformationAtom);
  const setAuthzContext = useSetRecoilState(authzContextAtom);

  const context = useMemo(
    () => buildAuthzContext(userInfo, extractGrantsFromUser(userInfo)),
    [userInfo]
  );

  useEffect(() => {
    setAuthzContext(context);
  }, [context, setAuthzContext]);

  const value = useMemo(() => ({
    context,
    can: (requirement) => canAccess(context, requirement),
    canAny: (requirements) => canAccessAny(context, requirements),
    canResource: (params) => canAccessResource(context, params),
    isFullAccess: context.isFullAccess,
    isAuthzV2Enabled: context.isAuthzV2Enabled,
  }), [context]);

  return (
    <AuthorizationContext.Provider value={value}>
      {children}
    </AuthorizationContext.Provider>
  );
}

export function useAuthorizationContext() {
  const value = React.useContext(AuthorizationContext);
  if (!value) {
    throw new Error('useAuthorizationContext must be used within AuthorizationProvider');
  }
  return value;
}

export function useAuthorization() {
  return useAuthorizationContext();
}

/**
 * Sync authz context when user info changes outside provider tree.
 * @param {object|null|undefined} userInfo
 */
export function syncAuthzContextFromUser(userInfo, setAuthzContext) {
  setAuthzContext(buildAuthzContext(userInfo, extractGrantsFromUser(userInfo)));
}

export { canAccess, canAccessAny, canAccessResource };
